import { inject } from 'mobx-react';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Container } from 'reactstrap';

import { modelOf } from '../../../prop-types';
import AdStore from '../../../store/AdStore';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import AdPages from '../../../types/AdPages';
import AdZones from '../../../types/AdZones';
import { blockTestRender } from '../../../util/process';
import SidebarLayout from '../../layout/SidebarLayout';
import SidebarSkeleton from '../SidebarSkeleton';

const ASPECT_RATIO = 2.85;

const HomePageSkeleton = ({ adStore, configStore, uiStore }) => {
  const mainBannerWithinContent =
    configStore.banner.mainBanner.withinContent &&
    !configStore.banner.mainBanner.fullWidth;

  // Match HomePage.shouldHaveMainAds — frontPageAds only exposes zone ids.
  const shouldHaveMainAds = adStore.shouldShowZone(
    AdPages.FRONT_PAGE,
    AdZones.MAIN_BANNER,
    uiStore.isMobile
  );

  const getBannerHeight = () => {
    if (uiStore.isMobile) {
      return typeof window !== 'undefined'
        ? window.innerWidth / ASPECT_RATIO
        : configStore.banner.mainBanner.height;
    }

    return configStore.banner.mainBanner.height;
  };

  const banner = shouldHaveMainAds ? (
    <div className="HomePageSkeleton__banner">
      {blockTestRender() && <Skeleton height={getBannerHeight()} />}
    </div>
  ) : null;

  const mobileContent = (
    <div className="HomePageSkeleton__content">
      {mainBannerWithinContent && banner}
      {mainBannerWithinContent && shouldHaveMainAds && (
        <div className="Skeleton__spacer" />
      )}
      <div className="Skeleton__spacer" />
      <div className="SectionPage__section-content">
        {blockTestRender() && <Skeleton height={42} />}
      </div>
      <div className="Skeleton__spacer" />
      {blockTestRender() && <Skeleton height={28} width={220} />}
      <div className="Skeleton__spacer" />
      {blockTestRender() && <Skeleton height={400} />}
      <div className="Skeleton__spacer" />
      {blockTestRender() && <Skeleton height={28} width={220} />}
      <div className="Skeleton__spacer" />
      {blockTestRender() && <Skeleton height={400} />}
    </div>
  );

  const desktopContent = (
    <div className="HomePageSkeleton__content">
      {mainBannerWithinContent && banner}
      {mainBannerWithinContent && shouldHaveMainAds && (
        <div className="Skeleton__spacer" />
      )}
      <div className="Skeleton__spacer" />
      <div className="SectionPage__section-content">
        {blockTestRender() && <Skeleton height={160} />}
      </div>
      <div className="Skeleton__spacer" />
      {blockTestRender() && <Skeleton height={48} width={320} />}
      <div className="Skeleton__spacer" />
      {blockTestRender() && <Skeleton height={640} />}
    </div>
  );

  const content = uiStore.isMobile ? mobileContent : desktopContent;

  return (
    <>
      {!mainBannerWithinContent && banner}
      <Container className="HomePage__container">
        {configStore.navigation.sidebar.enabled ? (
          <SidebarLayout
            className="HomePageSkeleton"
            sidebarPlaceHolder={!uiStore.isMobile ? <SidebarSkeleton /> : null}
            content={content}
            contentPlaceHolder={content}
          />
        ) : (
          <div className="HomePageSkeleton HomePageSkeleton--full-width">
            {content}
          </div>
        )}
      </Container>
    </>
  );
};

HomePageSkeleton.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
};

export default inject('adStore', 'configStore', 'uiStore')(HomePageSkeleton);
